import { FetchRequest, FetchResponse } from '@rails/request.js'
import { SubmissionFormType } from '../types/common'

export type PriorityOption = '' | 'green_flag' | 'yellow_flag' | 'red_flag'

interface AddToContainerArgs {
  accountNumber?: string
  containerSessionId?: string
  submissionContainerId: string
  submissionViewId: string
}

interface CreateArgs {
  accountName: null | string
  accountNumber: null | string
  containerSessionId?: null | string
  formType: SubmissionFormType
  repCode: null | string
  submissionContainerId: null | string // Specific to New Business
  submissionViewId: null | string
}

// TODO: Properly type this API response
// lock and review_session are null when the advisor is view only on the new
// submission; the whole body is { error } on a 422.
interface CreateResponse extends FetchResponse {
  get json(): Promise<{
    error?: string
    lock: null | Record<string, unknown>
    review_session: null | Record<string, unknown>
    submission: Record<string, unknown>
  }>
}

interface DestroyArgs {
  id: string
}

interface LoadedSubmission {
  account_number: string
  category: string
  container_id: string
  created_at: string
  description: string
  form_type: string
  id: string
  legacy_account_number: null | string
  legacy_manager_code: null | string
  rep_code_value: string
  priority: null | PriorityLevel
  type: string
  user_name: string
  viewable: boolean
}

interface LoadResponse extends FetchResponse {
  get json(): Promise<LoadedSubmission[]>
}

interface SearchArgs {
  ids: string[]
  query: string
}

interface UpdatePriorityArgs {
  priority: PriorityOption
  submissionId: string
}

interface UnlockArgs {
  identityId: string
  lockId: string
  submissionId: string
}

// Adds a draft of the given type to one account group. The response streams in its card.
export function addToContainer({
  accountNumber,
  containerSessionId,
  submissionContainerId,
  submissionViewId,
}: AddToContainerArgs) {
  const endpoint = `/submission_containers/${submissionContainerId}/submissions`
  const body = new FormData()
  body.append('submission_view_id', submissionViewId)
  if (accountNumber) body.append('account_number', accountNumber)
  // Which tab is asking, so the new card comes back open for editing in it.
  if (containerSessionId) body.append('container_session_id', containerSessionId)

  return new FetchRequest('post', endpoint, { body, responseKind: 'turbo-stream' }).perform()
}

export function create(args: CreateArgs): Promise<CreateResponse> {
  const endpoint = '/submissions'
  const body = JSON.stringify({
    account_name: args.accountName,
    account_number: args.accountNumber,
    container_session_id: args.containerSessionId,
    form_type: args.formType,
    rep_code_attributes: { value: args.repCode },
    submission_container_id: args.submissionContainerId,
    submission_view_id: args.submissionViewId,
  })

  return new FetchRequest('post', endpoint, { body }).perform()
}

export function destroy({ id }: DestroyArgs) {
  const endpoint = `/submissions/${id}`

  return new FetchRequest('delete', endpoint, { responseKind: 'turbo-stream' }).perform()
}

export function load(): Promise<LoadResponse> {
  const endpoint = `/api/submissions?onload=true`

  return new FetchRequest('get', endpoint).perform()
}

export function search({ ids, query }: SearchArgs) {
  const params = new URLSearchParams({
    search: query,
    submissionId: JSON.stringify(ids),
  })
  const endpoint = `/api/submissions?${params}`

  return new FetchRequest('get', endpoint).perform()
}

export function updatePriority({ priority, submissionId }: UpdatePriorityArgs) {
  const endpoint = `/submissions/priority/${submissionId}`

  return new FetchRequest('patch', endpoint, { body: JSON.stringify({ priority: priority || null }) }).perform()
}

export function unlock({ identityId, lockId, submissionId }: UnlockArgs) {
  const params = new URLSearchParams({ identity_id: identityId })
  const endpoint = `/submissions/${submissionId}/locks/${lockId}?${params}`

  // keepalive lets the request survive page unload — see app/frontend/locking.js#closeLocking
  return new FetchRequest('delete', endpoint, { keepalive: true }).perform()
}
